import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Plus, Copy, Trash2, ExternalLink, Loader2, Newspaper, Lock, Globe,
} from "lucide-react";

/**
 * Admin-only console for authoring hidden press releases.
 *
 * Every release lives at a public URL `/press/{unguessable-slug}` — no listing
 * exists on the marketing site, and unpublished drafts 404 for anyone hitting
 * their URL. Admin uses the "Copy link" button to share a release privately
 * with a journalist.
 */
export default function AdminPress() {
  const { user } = useAuth();
  const [releases, setReleases] = useState(null);
  const [editing, setEditing] = useState(null);   // full release doc when editing, {mode:"new"} when creating
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", published: false });

  const load = async () => {
    try {
      const { data } = await http.get("/admin/press");
      setReleases(data.releases || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load releases");
    }
  };

  useEffect(() => { load(); }, []);

  if (user === false || user?.role !== "admin") return <Navigate to="/login" />;

  const openNew = () => {
    setForm({ title: "", body: "", published: false });
    setEditing({ mode: "new" });
  };
  const openEdit = (r) => {
    setForm({ title: r.title, body: r.body || "", published: !!r.published });
    setEditing(r);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editing.mode === "new") {
        await http.post("/admin/press", form);
        toast.success("Press release created");
      } else {
        await http.patch(`/admin/press/${editing.id}`, form);
        toast.success("Press release updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (r) => {
    try {
      await http.patch(`/admin/press/${r.id}`, { published: !r.published });
      toast.success(r.published ? "Unpublished" : "Published — copy the link to share");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This can't be undone.`)) return;
    try {
      await http.delete(`/admin/press/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const publicUrl = (slug) => `${window.location.origin}/press-releases/${slug}`;

  const copyLink = async (r) => {
    try {
      await navigator.clipboard.writeText(publicUrl(r.slug));
      toast.success("Public link copied");
    } catch {
      toast.error("Clipboard blocked — copy the URL manually");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-press-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Newspaper className="w-5 h-5 text-indigo-600" />
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Admin</div>
              <div className="font-display font-bold text-lg leading-none mt-1">Press releases</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={openNew}
              data-testid="admin-press-new"
              className="rounded-full bg-zinc-900 hover:bg-zinc-800"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New release
            </Button>
            <Link to="/admin">
              <Button variant="ghost" className="rounded-full text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Pipeline
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex items-start gap-3">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Hidden by design.</strong> Releases have no `/press-releases` index and every page carries
            `noindex, nofollow`. Only people you send the URL to can reach a published release.
            Unpublished drafts 404 even at their own slug.
          </div>
        </div>

        {releases === null ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : releases.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
            No press releases yet — click <strong>New release</strong> to write your first.
          </div>
        ) : (
          <div className="rounded-xl bg-white border border-zinc-200 divide-y divide-zinc-100 overflow-hidden" data-testid="admin-press-list">
            {releases.map((r) => (
              <div key={r.id} className="p-5 flex items-start justify-between gap-4" data-testid={`press-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openEdit(r)}
                      className="font-display font-bold text-lg text-zinc-900 hover:text-indigo-600 text-left"
                    >
                      {r.title}
                    </button>
                    {r.published ? (
                      <Badge className="bg-emerald-600 text-white"><Globe className="w-3 h-3 mr-1" /> Published</Badge>
                    ) : (
                      <Badge variant="outline" className="border-zinc-300 text-zinc-600"><Lock className="w-3 h-3 mr-1" /> Draft</Badge>
                    )}
                  </div>
                  <div className="mt-1.5 text-xs font-mono text-zinc-500 break-all">
                    /press-releases/{r.slug}
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">
                    Updated {new Date(r.updated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    {r.published_at && ` · First published ${new Date(r.published_at).toLocaleString(undefined, { dateStyle: "medium" })}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyLink(r)}
                    disabled={!r.published}
                    title={r.published ? "Copy public URL" : "Publish first to enable link"}
                    data-testid={`press-copy-${r.id}`}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
                  </Button>
                  {r.published && (
                    <a href={publicUrl(r.slug)} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" title="Open in new tab">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  <div className="flex items-center gap-1.5" title={r.published ? "Unpublish (URL will 404)" : "Publish (URL becomes reachable)"}>
                    <Switch
                      checked={r.published}
                      onCheckedChange={() => togglePublish(r)}
                      data-testid={`press-publish-${r.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(r)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`press-delete-${r.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing && (
        <PressEditor
          mode={editing.mode || "edit"}
          form={form}
          setForm={setForm}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}


function PressEditor({ mode, form, setForm, saving, onClose, onSave }) {
  return (
    <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="press-editor">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">
              {mode === "new" ? "New" : "Edit"}
            </div>
            <div className="font-display font-bold text-lg mt-1">Press release</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="p-6 space-y-5 overflow-y-auto">
          <div>
            <Label htmlFor="press-title">Headline</Label>
            <Input
              id="press-title"
              data-testid="press-editor-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Affra launches Founding Partner programme"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="press-body">Body</Label>
            <Textarea
              id="press-body"
              data-testid="press-editor-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Use Markdown — # headings, **bold**, [links](https://…), lists, etc."
              className="mt-1.5 min-h-[280px] font-mono text-sm"
              rows={14}
            />
            <div className="mt-1.5 text-xs text-zinc-500">
              Markdown supported: headings, bold, links, lists, blockquotes. Renders on the public page as clean typography.
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl bg-zinc-50 border border-zinc-200 p-4">
            <Switch
              checked={form.published}
              onCheckedChange={(v) => setForm({ ...form, published: v })}
              data-testid="press-editor-published"
            />
            <div className="text-sm">
              <div className="font-semibold">
                {form.published ? "Published — reachable at the public URL" : "Draft — public URL will 404"}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                A URL only exists once you save. Unpublishing later makes the URL 404 again.
              </div>
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-zinc-100 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="press-editor-save" className="bg-zinc-900 hover:bg-zinc-800">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (mode === "new" ? "Create release" : "Save changes")}
          </Button>
        </div>
      </div>
    </div>
  );
}
